import {
  Ban,
  Check,
  Clock,
  CornerUpLeft,
  MessageCircle,
  MoreVertical,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  Send,
  Settings2,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ApiError, apiFetch } from "../../lib/api";
import { chatIdentity } from "../../lib/chat-identity";
import { useAdmin } from "./AdminShell";
import type { AdminChatBan, AdminChatMessage } from "./types";

const timeoutChoices = [
  ["5", "5 Min"],
  ["30", "30 Min"],
  ["1440", "24 Std"],
] as const;

const slowModePresets = [
  [0, "Aus – jeder kann sofort schreiben"],
  [10, "10 Sekunden Pause"],
  [30, "30 Sekunden Pause"],
  [60, "1 Minute Pause"],
  [300, "5 Minuten Pause"],
] as const;

const slowModeLabel = (seconds: number) =>
  slowModePresets.find(([preset]) => preset === seconds)?.[1];

export function AdminChatPage({
  embedded = false,
  eventId: lockedEventId,
}: { embedded?: boolean; eventId?: string } = {}) {
  const { data, refresh } = useAdmin();
  const [eventId, setEventId] = useState(lockedEventId ?? "");
  const [messages, setMessages] = useState<AdminChatMessage[]>([]);
  const [bans, setBans] = useState<AdminChatBan[]>([]);
  const [state, setState] = useState<{
    chatEnabled: boolean;
    chatPaused: boolean;
  }>({ chatEnabled: false, chatPaused: false });
  const [filter, setFilter] = useState<"visible" | "removed">("visible");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [composerNote, setComposerNote] = useState("");
  const feedRef = useRef<HTMLElement | null>(null);
  // Antwort-Bezug: im LiveDesk wie im Composer-Chip sichtbar, wird mit
  // der nächsten Nachricht als replyToId reisend mitgeschickt.
  const [replyTarget, setReplyTarget] = useState<AdminChatMessage | null>(null);

  const currentEvent =
    data.events.find((event) => event.id === eventId) ??
    data.events.find((event) => event.status === "live") ??
    data.events[0];

  const loadBanList = async () => {
    try {
      setBans(await apiFetch<AdminChatBan[]>("/api/admin/chat/bans/active"));
    } catch {
      setError("Sperren konnten nicht geladen werden.");
    }
  };

  const load = async () => {
    if (!currentEvent) return;
    try {
      const response = await apiFetch<{
        messages: AdminChatMessage[];
      }>(
        `/api/admin/chat/events/${currentEvent.id}/messages?state=${filter}&limit=200`,
      );
      // Chronologisch sortieren wie im Reader/Widget: die neuesten
      // Nachrichten stehen unten (die Admin-API liefert Newest-First).
      setMessages(
        [...response.messages].sort((a, b) =>
          `${a.createdAt}\u0000${a.id}`.localeCompare(
            `${b.createdAt}\u0000${b.id}`,
          ),
        ),
      );
      setError("");
    } catch (caught) {
      // 429 (Rate-Limit) ist vorübergehend – kein roter Fehler im Live-Betrieb.
      if (!(caught instanceof ApiError && caught.status === 429)) {
        setError("Chat-Nachrichten konnten nicht geladen werden.");
      }
    }
  };

  const chatEventId = currentEvent?.id;

  // Wichtig mit Dependencies: ohne Array lief der Polling-Effekt bei jedem
  // Render und hat die Admin-API in einer Fetch-Schleife in den Rate-Limit
  // (429) getrieben.
  useEffect(() => {
    if (!chatEventId) return;
    void load();
    const interval = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      void load();
    }, 10_000);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatEventId, filter]);

  // Sticky Scroll wie im Widget: nur nachziehen, wer unten war.
  useEffect(() => {
    const feed = feedRef.current;
    if (!feed) return;
    const stuck = feed.scrollHeight - feed.scrollTop - feed.clientHeight < 60;
    if (stuck) feed.scrollTop = feed.scrollHeight;
  }, [messages.length]);

  useEffect(() => {
    if (!openMenu) return;
    const close = () => setOpenMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [openMenu]);

  useEffect(() => {
    if (!settingsOpen) return;
    const close = () => setSettingsOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [settingsOpen]);

  useEffect(() => {
    void loadBanList();
  }, []);

  // Als Redaktion mitschreiben: die öffentliche Chat-Route akzeptiert
  // jede eingeloggte Sitzung; das Badge markiert den Autor als Redaktion.
  const sendAsEditor = async () => {
    const content = draft.trim();
    if (!currentEvent || content.length < 1) return;
    setSending(true);
    setComposerNote("");
    try {
      await apiFetch(`/api/public/events/${currentEvent.slug}/chat/messages`, {
        method: "POST",
        body: JSON.stringify({
          content,
          replyToId:
            replyTarget?.state === "visible" ? replyTarget.id : undefined,
          clientMutationId:
            window.crypto?.randomUUID?.() ?? `redaktion-${Date.now()}`,
        }),
      });
      setDraft("");
      setReplyTarget(null);
      await load();
    } catch (caught) {
      const apiError = caught as ApiError;
      const wait = apiError.details as { waitSeconds?: number } | undefined;
      setComposerNote(
        apiError.code === "CHAT_SLOW_MODE"
          ? `Slow-Modus: ${Math.ceil(wait?.waitSeconds ?? 0)} s warten`
          : apiError.code === "CHAT_CAPTCHA_REQUIRED"
            ? "Erste Nachricht muss im Markt-Chat per Captcha bestätigt werden – einmal dort senden, danach schreibt der Live-Desk frei."
            : apiError.message || "Nachricht konnte nicht gesendet werden.",
      );
    } finally {
      setSending(false);
    }
  };

  if (!currentEvent) {
    return (
      <div className="admin-page chat-admin">
        {embedded ? (
          <header className="admin-page-header">
            <h2>Chat</h2>
          </header>
        ) : (
          <h2>Chat</h2>
        )}
        <p>Lege zuerst ein Event an.</p>
      </div>
    );
  }

  const patchChatSettings = async (patch: {
    chatEnabled?: boolean;
    chatPaused?: boolean;
    chatSlowModeSeconds?: number;
  }) => {
    await apiFetch(`/api/admin/events/${currentEvent.id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    await refresh();
  };

  const [slowModeInput, setSlowModeInput] = useState(
    String(currentEvent.chatSlowModeSeconds ?? 0),
  );
  useEffect(() => {
    setSlowModeInput(String(currentEvent.chatSlowModeSeconds ?? 0));
  }, [currentEvent.id, currentEvent.chatSlowModeSeconds]);

  const saveSlowMode = async (seconds: number) => {
    const clamped = Math.max(0, Math.min(3600, Math.round(seconds)));
    setBusy("slow-mode");
    try {
      await patchChatSettings({ chatSlowModeSeconds: clamped });
      setError("");
    } catch {
      setError("Slow-Modus konnte nicht gespeichert werden.");
    } finally {
      setBusy("");
    }
  };

  const isPresetSeconds = (value: number) =>
    slowModePresets.some(([preset]) => preset === value);
  const slowModeSelectValue = isPresetSeconds(
    currentEvent.chatSlowModeSeconds ?? 0,
  )
    ? String(currentEvent.chatSlowModeSeconds ?? 0)
    : "custom";

  const removeMessage = async (message: AdminChatMessage) => {
    setBusy(message.id);
    try {
      await apiFetch(`/api/admin/chat/messages/${message.id}/remove`, {
        method: "POST",
      });
      await Promise.all([load(), loadBanList()]);
    } catch {
      setError("Nachricht konnte nicht entfernt werden.");
    } finally {
      setBusy("");
    }
  };

  const timeoutUser = async (message: AdminChatMessage, minutes: number) => {
    if (!message.authorUserId) return;
    setBusy(message.id);
    try {
      await apiFetch(`/api/admin/chat/events/${currentEvent.id}/timeout`, {
        method: "POST",
        body: JSON.stringify({
          userId: message.authorUserId,
          minutes,
          reason: `Chat-Time ${message.authorName}`,
        }),
      });
      await Promise.all([load(), loadBanList()]);
    } catch {
      setError("Timeout konnte nicht gesetzt werden.");
    } finally {
      setBusy("");
    }
  };

  const banUser = async (message: AdminChatMessage) => {
    if (!message.authorUserId) return;
    setBusy(message.id);
    try {
      await apiFetch("/api/admin/chat/bans", {
        method: "POST",
        body: JSON.stringify({
          kind: "user",
          value: message.authorUserId,
          reason: `Dauerhaft gesperrt (${message.authorName})`,
        }),
      });
      await Promise.all([load(), loadBanList()]);
    } catch {
      setError("Sperre konnte nicht gesetzt werden.");
    } finally {
      setBusy("");
    }
  };

  const banIp = async (message: AdminChatMessage) => {
    if (!message.authorIpHash) return;
    setBusy(message.id);
    try {
      await apiFetch("/api/admin/chat/bans", {
        method: "POST",
        body: JSON.stringify({
          kind: "ip",
          value: message.authorIpHash,
          reason: `IP-ID ${message.authorName.slice(0, 20)}`,
        }),
      });
      await Promise.all([load(), loadBanList()]);
    } catch {
      setError("IP-Sperre konnte nicht gesetzt werden.");
    } finally {
      setBusy("");
    }
  };

  const unban = async (ban: AdminChatBan) => {
    setBusy(ban.id);
    try {
      await apiFetch(`/api/admin/chat/bans/${ban.id}`, { method: "DELETE" });
      await Promise.all([load(), loadBanList()]);
    } catch {
      setError("Unban fehlgeschlagen.");
    } finally {
      setBusy("");
    }
  };

  if (embedded) {
    const statusLabel = !currentEvent.chatEnabled
      ? "Chat aus"
      : currentEvent.chatPaused
        ? "Pausiert"
        : "Aktiv";
    const live = currentEvent.chatEnabled && !currentEvent.chatPaused;

    return (
      <div className="admin-page chat-admin chat-admin--live">
        <header className="chat-admin__bar">
          <span className={`chat-admin__status${live ? " is-on" : ""}`}>
            <i /> {statusLabel}
          </span>
          <div className="chat-admin__bar-actions">
            <button
              className="icon-action"
              aria-label="Chat aktualisieren"
              onClick={() => void load()}
            >
              <RefreshCw size={18} />
            </button>
            <div className="chat-admin__menu-anchor">
              <button
                className="icon-action"
                aria-label="Chat-Einstellungen"
                aria-expanded={settingsOpen}
                onClick={(click) => {
                  click.stopPropagation();
                  setSettingsOpen(!settingsOpen);
                }}
              >
                <Settings2 size={18} />
              </button>
              {settingsOpen && (
                <div
                  className="chat-admin__dropdown chat-admin__dropdown--panel"
                  onClick={(click) => click.stopPropagation()}
                >
                  <button
                    className="chat-admin__menu-item"
                    disabled={!!busy}
                    onClick={() =>
                      void patchChatSettings({
                        chatEnabled: !currentEvent.chatEnabled,
                      })
                    }
                  >
                    {currentEvent.chatEnabled ? (
                      <>
                        <X size={15} /> Chat deaktivieren
                      </>
                    ) : (
                      <>
                        <PlayCircle size={15} /> Chat aktivieren
                      </>
                    )}
                  </button>
                  <button
                    className="chat-admin__menu-item"
                    disabled={!!busy || !currentEvent.chatEnabled}
                    onClick={() =>
                      void patchChatSettings({
                        chatPaused: !currentEvent.chatPaused,
                      })
                    }
                  >
                    {currentEvent.chatPaused ? (
                      <>
                        <PlayCircle size={15} /> Weiter laufen lassen
                      </>
                    ) : (
                      <>
                        <PauseCircle size={15} /> Pausieren
                      </>
                    )}
                  </button>
                  <div className="chat-admin__menu-sep" />
                  <label className="chat-admin__menu-field">
                    <span>Slow-Modus</span>
                    <select
                      value={slowModeSelectValue}
                      disabled={busy === "slow-mode"}
                      onChange={(change) => {
                        const value = change.target.value;
                        if (value === "custom") return;
                        void saveSlowMode(Number(value));
                      }}
                    >
                      {slowModePresets.map(([seconds, label]) => (
                        <option key={seconds} value={seconds}>
                          {label}
                        </option>
                      ))}
                      {slowModeSelectValue === "custom" && (
                        <option value="custom">
                          Eigener Wert ({currentEvent.chatSlowModeSeconds}{" "}
                          Sekunden)
                        </option>
                      )}
                    </select>
                  </label>
                  <div className="chat-admin__menu-sep" />
                  <button
                    className="chat-admin__menu-item"
                    aria-pressed={filter === "removed"}
                    onClick={() =>
                      setFilter(filter === "removed" ? "visible" : "removed")
                    }
                  >
                    {filter === "removed" ? (
                      <>
                        <Check size={15} /> Entfernte ausblenden
                      </>
                    ) : (
                      <>
                        <Trash2 size={15} /> Entfernte anzeigen
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        {error && <p role="alert">{error}</p>}

        <section className="chat-admin__feed" ref={feedRef}>
          {messages.map((message) => {
            const own =
              message.authorUserId && data.user.id
                ? message.authorUserId === data.user.id
                : false;
            return (
              <article
                key={message.id}
                className={`chat-admin__entry${own ? " chat-admin__entry--own" : ""}`}
                data-removed={message.state === "removed"}
              >
                <span
                  className="submission-avatar"
                  style={{
                    background: chatIdentity(
                      message.authorName,
                      message.authorUserId,
                    ).color,
                  }}
                >
                  {
                    chatIdentity(message.authorName, message.authorUserId)
                      .initials
                  }
                </span>
                <div className="chat-admin__entry-body">
                  <header>
                    <strong>{message.authorName}</strong>
                    <small>
                      {new Intl.DateTimeFormat("de", {
                        hour: "2-digit",
                        minute: "2-digit",
                      }).format(new Date(message.createdAt))}
                      {message.timedOutUntil ? " · Time" : ""}
                      {message.state === "removed" ? " · entfernt" : ""}
                    </small>
                    {message.editor && (
                      <span className="chat-admin__badge">Redaktion</span>
                    )}
                  </header>
                  {message.replyTo && (
                    <blockquote className="chat-admin__replyquote">
                      <strong>{message.replyTo.authorName}</strong>
                      <span>{message.replyTo.content}</span>
                    </blockquote>
                  )}
                  <p>{message.content}</p>
                </div>
                {message.state === "visible" &&
                  !currentEvent.chatPaused &&
                  currentEvent.chatEnabled && (
                    <button
                      className="chat-admin__reply"
                      onClick={() => setReplyTarget(message)}
                      aria-label={`Auf Nachricht von ${message.authorName} antworten`}
                      title="Antworten"
                    >
                      <CornerUpLeft size={14} />
                    </button>
                  )}
                {message.state === "visible" &&
                  (message.authorUserId || message.authorIpHash) && (
                    <div className="chat-admin__menu-anchor">
                      <button
                        className="chat-admin__kebab"
                        aria-label={`Nachricht von ${message.authorName} moderieren`}
                        aria-expanded={openMenu === message.id}
                        onClick={(click) => {
                          click.stopPropagation();
                          setOpenMenu(
                            openMenu === message.id ? null : message.id,
                          );
                        }}
                      >
                        <MoreVertical size={16} />
                      </button>
                      {openMenu === message.id && (
                        <div
                          className="chat-admin__dropdown"
                          onClick={(click) => click.stopPropagation()}
                        >
                          <button
                            className="chat-admin__menu-item chat-admin__menu-item--danger"
                            disabled={!!busy}
                            onClick={() => {
                              setOpenMenu(null);
                              void removeMessage(message);
                            }}
                          >
                            <X size={15} /> Entfernen
                          </button>
                          {message.authorUserId && (
                            <>
                              <div className="chat-admin__menu-sep" />
                              {timeoutChoices.map(([minutes, label]) => (
                                <button
                                  key={minutes}
                                  className="chat-admin__menu-item"
                                  disabled={!!busy}
                                  onClick={() => {
                                    setOpenMenu(null);
                                    void timeoutUser(message, Number(minutes));
                                  }}
                                >
                                  <Clock size={15} /> {label} Time
                                </button>
                              ))}
                              <div className="chat-admin__menu-sep" />
                              <button
                                className="chat-admin__menu-item"
                                disabled={!!busy}
                                onClick={() => {
                                  setOpenMenu(null);
                                  void banUser(message);
                                }}
                              >
                                <Ban size={15} /> Nutzer dauerhaft sperren
                              </button>
                            </>
                          )}
                          {message.authorIpHash && (
                            <button
                              className="chat-admin__menu-item"
                              disabled={!!busy}
                              onClick={() => {
                                setOpenMenu(null);
                                void banIp(message);
                              }}
                            >
                              <Ban size={15} /> IP sperren
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
              </article>
            );
          })}
          {!messages.length && (
            <div className="admin-empty chat-admin__feed-empty">
              <MessageCircle />
              <p>Noch keine Chat-Nachrichten.</p>
            </div>
          )}
        </section>
        {currentEvent.chatEnabled && (
          <form
            className="chat-admin__composer"
            onSubmit={(submit) => {
              submit.preventDefault();
              void sendAsEditor();
            }}
          >
            {replyTarget && (
              <p className="chat-admin__replyline">
                Antwort an <strong>{replyTarget.authorName}</strong>
                <button
                  type="button"
                  aria-label="Antwort verwerfen"
                  onClick={() => setReplyTarget(null)}
                >
                  ×
                </button>
              </p>
            )}
            <input
              value={draft}
              onChange={(change) => setDraft(change.target.value)}
              maxLength={500}
              placeholder="Als Redaktion ins Live-Chat schreiben…"
              aria-label="Als Redaktion schreiben"
              disabled={sending}
            />
            <button type="submit" disabled={sending || !draft.trim()}>
              <Send size={15} /> {sending ? "Sendet…" : "Senden"}
            </button>
            {composerNote && (
              <p className="chat-admin__composer-note" role="status">
                {composerNote}
              </p>
            )}
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="admin-page chat-admin">
      {embedded ? (
        <header className="admin-page-header">
          <h2>Chat-Moderation</h2>
          <button
            className="icon-action"
            aria-label="Chat aktualisieren"
            onClick={() => void load()}
          >
            <RefreshCw size={16} />
          </button>
        </header>
      ) : (
        <header className="admin-page-header">
          <div>
            <h1>Chat-Moderation</h1>
            <p>
              Nachrichten, Sperren und Pausen für den Live-Chat – nachzulesen im
              Reader.
            </p>
          </div>
          <button
            className="icon-action"
            aria-label="Aktualisieren"
            onClick={() => void load()}
          >
            <RefreshCw />
          </button>
        </header>
      )}
      {error && <p role="alert">{error}</p>}

      <section
        className={`settings-panel settings-panel--wide${embedded ? " chat-admin--panel" : ""}`}
      >
        <header hidden={embedded}>
          <h2>Event-Chat</h2>
          <MessageCircle />
        </header>
        <div className="settings-form">
          <label className="wide" hidden={embedded}>
            <span>Event</span>
            <select
              value={currentEvent.id}
              onChange={(change) => setEventId(change.target.value)}
            >
              {data.events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title} ({event.status})
                </option>
              ))}
            </select>
          </label>
          <div className="chat-admin__actions">
            <button
              onClick={() =>
                void patchChatSettings({
                  chatEnabled: !currentEvent.chatEnabled,
                })
              }
              className={currentEvent.chatEnabled ? "button-live" : ""}
            >
              {currentEvent.chatEnabled ? (
                <>
                  <Check /> Chat aktiv
                </>
              ) : (
                <>
                  <PlayCircle /> Chat an
                </>
              )}
            </button>
            <button
              onClick={() =>
                void patchChatSettings({
                  chatPaused: !currentEvent.chatPaused,
                })
              }
              disabled={!currentEvent.chatEnabled}
            >
              {currentEvent.chatPaused ? (
                <>
                  <PlayCircle /> Weiter laufen lassen
                </>
              ) : (
                <>
                  <PauseCircle /> Pausieren
                </>
              )}
            </button>
          </div>
          <div className="settings-form" aria-label="Slow-Modus">
            <label className="wide">
              <span>Slow-Modus – Wartezeit pro Person</span>
              <div className="chat-admin__slowmode-row">
                <select
                  value={slowModeSelectValue}
                  disabled={busy === "slow-mode"}
                  onChange={(change) => {
                    const value = change.target.value;
                    if (value === "custom") {
                      setSlowModeInput(
                        String(currentEvent.chatSlowModeSeconds ?? 0),
                      );
                      return;
                    }
                    void saveSlowMode(Number(value));
                  }}
                >
                  {slowModePresets.map(([seconds, label]) => (
                    <option key={seconds} value={seconds}>
                      {label}
                    </option>
                  ))}
                  <option value="custom">Eigener Wert…</option>
                </select>
                {slowModeSelectValue === "custom" && (
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={3600}
                    value={slowModeInput}
                    onChange={(change) => setSlowModeInput(change.target.value)}
                  />
                )}
                {slowModeSelectValue === "custom" && (
                  <button
                    type="button"
                    disabled={!!busy}
                    onClick={() =>
                      void saveSlowMode(Number(slowModeInput) || 0)
                    }
                  >
                    <Check size={16} /> Übernehmen
                  </button>
                )}
              </div>
            </label>
            <small>
              {slowModeLabel(currentEvent.chatSlowModeSeconds ?? 0) ??
                `Eigener Wert: ${currentEvent.chatSlowModeSeconds} Sekunden Abstand zwischen Nachrichten pro Person.`}
            </small>
          </div>
        </div>
      </section>

      <div className="approval-filters">
        {(["visible", "removed"] as const).map((value) => (
          <button
            key={value}
            className={filter === value ? "active" : ""}
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
          >
            {value === "visible" ? "Sichtbar" : "Entfernt"}
          </button>
        ))}
      </div>

      <section className="approval-list chat-admin__messages">
        {messages.map((message) => (
          <article key={message.id} data-removed={message.state === "removed"}>
            <header>
              <span className="submission-avatar">
                {message.authorName.slice(0, 1).toUpperCase()}
              </span>
              <div>
                <strong>{message.authorName}</strong>
                <small>
                  {new Intl.DateTimeFormat("de", {
                    hour: "2-digit",
                    minute: "2-digit",
                  }).format(new Date(message.createdAt))}
                  {message.timedOutUntil ? " · Time" : ""}
                  {message.state === "removed" ? " · entfernt" : ""}
                </small>
              </div>
            </header>
            {message.replyTo && (
              <p className="chat-admin__replyquote-inline">
                ↩ {message.replyTo.authorName}: {message.replyTo.content}
              </p>
            )}
            <blockquote>{message.content}</blockquote>
            {message.state === "visible" && (
              <footer>
                <button
                  disabled={!!busy}
                  onClick={() => void removeMessage(message)}
                >
                  <X size={16} /> Entfernen
                </button>
                {message.authorUserId &&
                  timeoutChoices.map(([minutes, label]) => (
                    <button
                      key={minutes}
                      disabled={!!busy}
                      onClick={() => void timeoutUser(message, Number(minutes))}
                    >
                      <Clock size={16} /> {label}
                    </button>
                  ))}
                {message.authorUserId && (
                  <button
                    disabled={!!busy}
                    onClick={() => void banUser(message)}
                  >
                    <Ban size={16} /> Nutzer sperren
                  </button>
                )}
                {message.authorIpHash && (
                  <button disabled={!!busy} onClick={() => void banIp(message)}>
                    <Ban size={16} /> Sperren der IP unter
                  </button>
                )}
              </footer>
            )}
          </article>
        ))}
        {!messages.length && (
          <div className="admin-empty">
            <MessageCircle />
            <h2>Noch keine Chat-Nachrichten.</h2>
            <p>
              Aktiviere den Chat für ein Event, dann erscheinen hier die
              Beiträge der Leser.
            </p>
          </div>
        )}
      </section>

      <section
        className="settings-panel settings-panel--danger"
        hidden={embedded}
      >
        <header>
          <h2>Aktive Sperren</h2>
          <Ban />
        </header>
        <div className="member-list chat-admin__bans">
          {bans.map((ban) => (
            <article key={ban.id}>
              <span>{ban.kind === "user" ? "U" : "IP"}</span>
              <div>
                <small>{ban.value.slice(0, 16)}…</small>
                <small>
                  {ban.reason ?? "ohne Begründung"} ·{" "}
                  {ban.expiresAt
                    ? `bis ${new Intl.DateTimeFormat("de", {
                        hour: "2-digit",
                        minute: "2-digit",
                        day: "2-digit",
                        month: "2-digit",
                      }).format(new Date(ban.expiresAt))}`
                    : "dauerhaft"}
                </small>
              </div>
              <button
                className="icon-action"
                aria-label="Sperre aufheben"
                disabled={!!busy}
                onClick={() => void unban(ban)}
              >
                <Trash2 size={16} />
              </button>
            </article>
          ))}
          {!bans.length && (
            <p>
              Keine aktiven Sperren. Abgelaufene Timeouts bleiben historisch in
              der Datenbank.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
